import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, Plus, Search, Star, MessageSquareText, ImageIcon, Loader2, ShoppingBag } from "lucide-react";
import type { Product, Brand } from "@shared/schema";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { ShareDialog } from "@/components/share-dialog";
import { useToast } from "@/hooks/use-toast";
import { AdImage } from "@/components/ad-image";

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  draft: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  archived: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

const ROLE_STYLES: Record<string, string> = {
  hero: "bg-primary/10 text-primary",
  gateway: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  upsell: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  bundle: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  subscription: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
};

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => {
        if (i < full) {
          return <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />;
        }
        if (i === full && hasHalf) {
          return (
            <div key={i} className="relative h-3 w-3">
              <Star className="absolute h-3 w-3 text-amber-400/30" />
              <div className="absolute overflow-hidden w-[50%] h-3">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              </div>
            </div>
          );
        }
        return <Star key={i} className="h-3 w-3 text-amber-400/30" />;
      })}
    </div>
  );
}

interface ShopifyImportStatus {
  importId: string;
  status: "running" | "completed" | "failed" | "stalled";
  phase: string;
  imported: number;
  skipped: number;
  processed: number;
  failed: number;
  total: number;
  pagesFetched: number;
  catalogComplete: boolean;
  partialReason?: string | null;
  /** Task #2051 — products that got a Shopify variant list this run. */
  variantsAdded?: number;
  /**
   * Task #2368 — scraped items whose split-colorway sibling group already had
   * a product entry: merged into it instead of creating a duplicate.
   */
  siblingsMerged?: number;
  /** Task #2368 — colors appended from split-colorway sibling products. */
  siblingColorsAdded?: number;
  /** Task #2051 — per-product variant lookups that failed (fail-open). */
  variantFailures?: number;
  mediaTotal?: number;
  mediaCompleted?: number;
  mediaFailed?: number;
  lastProgressAt?: number | null;
  error: string | null;
}

export default function ProductsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(24);
  const [importProgress, setImportProgress] = useState<ShopifyImportStatus | null>(null);
  const { toast } = useToast();

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });

  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
  });

  const { data: reviewStats = {} } = useQuery<Record<string, { count: number; avgRating: number | null }>>({
    queryKey: [`/api/brands/${brandId}/product-review-stats`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
  });

  // Task #1761 — one-click Shopify import. The POST kicks off an async,
  // paginated storefront catalog import and returns an importId; we poll
  // the status endpoint inside mutationFn so isPending keeps the button's
  // in-progress state up until the import reaches a terminal state.
  const importMutation = useMutation({
    mutationFn: async (): Promise<ShopifyImportStatus> => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/products/import-shopify`);
      const { importId } = await res.json();
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const statusRes = await apiRequest(
          "GET",
          `/api/brands/${brandId}/products/import-shopify/${importId}`,
        );
        const status: ShopifyImportStatus = await statusRes.json();
        setImportProgress(status);
        // Catalog persistence is intentionally ahead of media enrichment.
        // Refresh on every poll so users see products arrive during the run.
        queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/products`] });
        if (status.status === "completed") return status;
        if (status.status === "failed" || status.status === "stalled") {
          throw new Error(status.error || "Import failed");
        }
      }
    },
    onSuccess: (status) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/products`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/product-review-stats`] });
      const variantsAdded = status.variantsAdded ?? 0;
      const variantFailures = status.variantFailures ?? 0;
      const parts: string[] = [];
      parts.push(
        status.imported > 0
          ? `Imported ${status.imported} product${status.imported === 1 ? "" : "s"}${status.skipped > 0 ? ` (${status.skipped} already existed)` : ""}.`
          : "No new products to import — everything was already here.",
      );
      if (variantsAdded > 0) {
        parts.push(`Variants added for ${variantsAdded} product${variantsAdded === 1 ? "" : "s"}.`);
      }
      // Task #2368 — split-colorway sibling merging (e.g. a garment whose
      // colors are split across several Shopify products): report appended
      // colors so a "nothing new imported" run still shows what changed.
      const siblingColorsAdded = status.siblingColorsAdded ?? 0;
      if (siblingColorsAdded > 0) {
        parts.push(
          `${siblingColorsAdded} color${siblingColorsAdded === 1 ? "" : "s"} added from sibling colorway products.`,
        );
      }
      if (variantFailures > 0) {
        parts.push(`${variantFailures} variant lookup${variantFailures === 1 ? "" : "s"} failed.`);
      }
      if ((status.mediaFailed ?? 0) > 0) {
        parts.push(
          `${status.mediaFailed} image${status.mediaFailed === 1 ? "" : "s"} could not be saved; existing durable images were preserved.`,
        );
      }
      if ((status.failed ?? 0) > 0) {
        parts.push(`${status.failed} product${status.failed === 1 ? "" : "s"} could not be processed.`);
      }
      if (!status.catalogComplete) {
        parts.push(status.partialReason || "Some catalog pages could not be fetched.");
      }
      const fullyComplete =
        status.catalogComplete &&
        (status.failed ?? 0) === 0 &&
        variantFailures === 0 &&
        (status.mediaFailed ?? 0) === 0;
      toast({
        title: fullyComplete
          ? "Shopify import complete"
          : "Shopify import partially complete",
        description: parts.join(" "),
        // This callback only handles completed imports. Partial completion
        // means optional enrichment was incomplete, not that the import failed.
        // True terminal failures remain destructive in onError below.
        variant: "default",
      });
    },
    onError: (err: any) => {
      // apiRequest throws "400: {json}" on non-OK; extract the server message.
      let message = err?.message || "Import failed";
      const jsonStart = message.indexOf("{");
      if (/^\d{3}: /.test(message) && jsonStart !== -1) {
        try {
          message = JSON.parse(message.slice(jsonStart)).message || message;
        } catch { /* keep raw message */ }
      }
      toast({
        title: "Shopify import failed",
        description: message,
        variant: "destructive",
      });
    },
    onSettled: () => setImportProgress(null),
  });

  const filtered = (products || []).filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <div className="fade-in-up flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground mt-1">Product library and strategy objects</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            className="rounded-full"
            onClick={() => navigate(`/brands/${brandId}/products/new`)}
            data-testid="button-create-product"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Product
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => importMutation.mutate()}
            disabled={importMutation.isPending}
            data-testid="button-import-shopify"
          >
            {importMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                {importProgress?.phase === "discovering"
                  ? `Discovering catalog${importProgress.pagesFetched > 0 ? ` (${importProgress.pagesFetched} pages)` : ""}…`
                  : importProgress?.phase === "mirroring"
                    ? `Saving images ${importProgress.mediaCompleted ?? 0}/${importProgress.mediaTotal ?? 0}…`
                  : importProgress?.total
                    ? `Importing ${importProgress.processed}/${importProgress.total}…`
                    : "Starting import…"}
              </>
            ) : (
              <>
                <ShoppingBag className="h-4 w-4 mr-1.5" />
                Import Shopify Products
              </>
            )}
          </Button>
        </div>
      </div>


      <div className="fade-in-up relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search products..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(24);
          }}
          className="pl-9 rounded-full"
          data-testid="input-search-products"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-56 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed p-16 text-center fade-in-up">
          <Package className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{search ? "No products match your search" : "No products yet"}</p>
        </div>
      ) : (
        <div className="space-y-4 fade-in-up-delay-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.slice(0, visibleCount).map(product => {
            const media = (product.mediaJson as string[] | null) || [];
            const heroImage = media[0];
            const stats = reviewStats[product.id];
            const roleStyle = ROLE_STYLES[(product.productRole || "").toLowerCase()] || "bg-gray-100 text-gray-600";
            const statusStyle = STATUS_STYLES[product.status || "draft"] || STATUS_STYLES.draft;

            return (
              <Link key={product.id} href={`/brands/${brandId}/products/${product.id}`}>
                <Card
                  className="premium-card cursor-pointer h-full group"
                  data-testid={`card-product-${product.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {heroImage ? (
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted shrink-0 border">
                          <AdImage
                            src={heroImage}
                            thumbWidth={160}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            placeholderClassName="!min-h-0 !min-w-0 text-center [&_svg]:h-5 [&_svg]:w-5"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-muted to-muted/50 shrink-0 border flex items-center justify-center">
                          <Package className="h-6 w-6 text-muted-foreground/30" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold truncate leading-tight" data-testid={`text-product-name-${product.id}`}>
                            {product.name}
                          </h3>
                          <div className="flex items-center gap-1 shrink-0" onClick={e => e.preventDefault()}>
                            {brand?.workspaceId && (
                              <ShareDialog
                                resourceType="product"
                                resourceId={product.id}
                                resourceName={product.name}
                                workspaceId={brand.workspaceId}
                                iconOnly
                              />
                            )}
                            <Badge className={`text-[10px] rounded-full px-2 ${statusStyle}`}>
                              {product.status}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          {product.productRole && (
                            <Badge className={`text-[10px] rounded-full px-2 py-0 ${roleStyle}`}>
                              {product.productRole}
                            </Badge>
                          )}
                          {product.priceText && (
                            <span className="text-sm font-semibold">${product.priceText}</span>
                          )}
                          {product.category && (
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider ml-auto">
                              {product.category}
                            </span>
                          )}
                        </div>

                        {stats && (stats.count > 0) && (
                          <div className="flex items-center gap-2" data-testid={`rating-product-${product.id}`}>
                            {stats.avgRating !== null && (
                              <>
                                <StarRating rating={stats.avgRating} />
                                <span className="text-xs font-medium">{stats.avgRating}</span>
                              </>
                            )}
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <MessageSquareText className="h-3 w-3" />
                              {stats.count} {stats.count === 1 ? "review" : "reviews"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {(product.summary || product.jobToBeDone) && (
                      <div className="mt-3 pt-3 border-t">
                        {product.summary ? (
                          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{product.summary}</p>
                        ) : product.jobToBeDone ? (
                          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed italic">"{product.jobToBeDone}"</p>
                        ) : null}
                      </div>
                    )}

                    {media.length > 1 && (
                      <div className="flex gap-1.5 mt-3 pt-3 border-t">
                        {media.slice(1, 6).map((url, i) => (
                          <div key={i} className="w-9 h-9 rounded-lg overflow-hidden bg-muted shrink-0 border">
                            <AdImage src={url} thumbWidth={72} alt="Product photo" className="w-full h-full object-cover" placeholderClassName="!min-h-0 !min-w-0 [&_svg]:h-4 [&_svg]:w-4 [&_span]:hidden" />
                          </div>
                        ))}
                        {media.length > 6 && (
                          <div className="w-9 h-9 rounded-lg bg-muted shrink-0 border flex items-center justify-center">
                            <span className="text-[10px] text-muted-foreground font-medium">+{media.length - 6}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
          </div>
          {filtered.length > visibleCount && (
            <Button type="button" variant="outline" className="w-full" onClick={() => setVisibleCount((count) => count + 24)} data-testid="button-more-products">
              Show 24 more products ({filtered.length - visibleCount} remaining)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
